/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/
// React
import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link, useOutletContext } from "react-router-dom";
// redux
import { useDispatch, useSelector } from "react-redux";
import { selectUser } from "../../../store/userSlice";
// actions
import { addInstance, updateInstance, deleteInstance } from "../../../actions";
// Base Theme
import _ from "../../../../@lodash";
import BASELoading from "../../../../@BASE/core/BASELoading";
// form libraries
import * as yup from "yup";
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// 3rd party libraries
import htmlToDraft from 'html-to-draftjs';
// material ui components
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Badge from "@mui/material/Badge";
// Form validation
import BasicInfoForm from "./form-parts/BasicInfo";
// import ContentForm from "./form-parts/Content";
import DevInfoForm from "./form-parts/DevInfo";
import AccessibilityForm from "./form-parts/Accessibility";
import ComponentsForm from "./form-parts/ComponentBreakdown";

// Form validation schema
const schema = yup.object().shape({
  title: yup.string().required("You must enter a title"),
  label: yup.string().required("You must enter a label"),
  classification: yup.string().required("You must enter a classification"),
  description: yup.string().required("You must provide rationale for this activity"),
  instructions: yup.array().of(
    yup.object().shape({
      title: yup.string().required("You must enter a name"),
      label: yup.string().required("You enter a class name"),
      course: yup.string().required("You must select a course"),
      content: yup.string().required("You must enter text"),
    })
  ),
  image: yup.string(),
  branching: yup.string().required('You must provide branching information for this activity').test(
    'is-html',
    'You must provide branching information for this activity',
    value => {
      try {
        // try to parse the HTML string to check if it's valid
        htmlToDraft(value);
        return true;
      } catch (e) {
        return false;
      }
    }
  ),
  dev_notes: yup.string().required('You must provide programming notes for this activity').test(
    'is-html',
    'You must provide programming notes for this activity',
    value => {
      try {
        // try to parse the HTML string to check if it's valid
        htmlToDraft(value);
        return true;
      } catch (e) {
        return false;
      }
    }
  ),
  ga_notes: yup.string().required('You must provide graphic artist notes for this activity').test(
    'is-html',
    'You must provide graphic artist notes for this activity',
    value => {
      try {
        // try to parse the HTML string to check if it's valid
        htmlToDraft(value);
        return true;
      } catch (e) {
        return false;
      }
    }
  ),
  accessibility: yup.string().required('You must enter accessibility information').test(
    'is-html',
    'You must enter accessibility information',
    value => {
      try {
        // try to parse the HTML string to check if it's valid
        htmlToDraft(value);
        return true;
      } catch (e) {
        return false;
      }
    }
  ),
  access_table: yup.array().of(
    yup.object().shape({
      element: yup.string(),
      items: yup.array().of(
        yup.object().shape({
          compliance: yup.string(),
          notes: yup.string(),
        })
      )
    })
  ),
  info: yup.string().required('You must provide information for this activity').test(
    'is-html',
    'You must provide information for this activity',
    value => {
      try {
        // try to parse the HTML string to check if it's valid
        htmlToDraft(value);
        return true;
      } catch (e) {
        return false;
      }
    }
  ),
  components: yup.array().of(
    yup.object().shape({
      image: yup.string(),
      component: yup.string(),
      elements: yup.array().of(
        yup.object().shape({
          title: yup.string(),
          attrs: yup.array().of(
            yup.object().shape({
              property: yup.string(),
              value: yup.string(),
            })
          )
        })
      )
    })
  )
});

function TemplateForm(props) {
  // instance context
  const { instance, handleInstanceAction, loading, setOnSave, setSaveDisabled } = useOutletContext();

  // form state
  const [tabValue, setTabValue] = useState(0);
  const [formLoading, setFormLoading] = useState(true);
  const user = useSelector(selectUser);
  // define dispatch
  const dispatch = useDispatch();
  //define navigate
  const navigate = useNavigate();
  // define route params
  const routeParams = useParams();
  // set state for the form
  const methods = useForm({
    mode: 'all',
    resolver: yupResolver(schema),
    defaultValues: {
      title: "",
      label: "",
      classification: "",
      description: "",
      instructions: [
        {
          title: "",
          label: "",
          course: "",
          rtl: false,
          content: "",
        },
      ],
      image: "",
      branching: "",
      dev_notes: "",
      ga_notes: "",
      accessibility: "",
      access_table: [
        { 
          element: "", 
          items: [
            { 
              compliance: "",
              notes: ""
            }
          ]
        }
      ],
      info: "",
      components: [
        {
          image: "",
          component: "",
          elements: [
            { 
              title: "",
              attrs: [
                {
                  property: "",
                  value: ""
                }
              ]
            }
          ]
        }
      ],
      content: {},
    },
  });
  const { reset, watch, control, onChange, formState, handleSubmit, setValue, getValues } = methods;
  // form state
  const { isValid, dirtyFields, errors } = formState;
  // on form change set form state
  function onSubmit(data) {
    // dispatch add or update course
    if (routeParams.id === 'new') {
      handleInstanceAction(addInstance('templates')(data), 'post');
    } else {
      handleInstanceAction(updateInstance('templates')(data), 'patch');
    }
  }

  // Pass the handleSubmit function to setOnSave
  useEffect(() => {
    setOnSave(() => handleSubmit(onSubmit));
  }, []);
  // Update the parent component with the form's validity and dirty state
  useEffect(() => {
    setSaveDisabled(_.isEmpty(dirtyFields) || !isValid);
  }, [formState]);
  // set state for the content schema
  useEffect(() => {
    let data = {
      ...instance,
      user: user._id,
    };
    reset({ ...data });
    setFormLoading(false);
  }, [instance, reset]);

  // const handleRemoveTemplate = () => {
  //   dispatch(removeTemplate(template.id.toString())).then(() => {
  //     navigate("/templates");
  //   });
  // };

  const handleTabChange = (event, value) => {
    setTabValue(value);
  }
  // error handling
  const basicErrors = formState.errors.title || formState.errors.label || formState.errors.classification || formState.errors.description || formState.errors.instructions || formState.errors.img;
  const devErrors = formState.errors.branching || formState.errors.dev_notes || formState.errors.ga_notes
  const accessibilityErrors = formState.errors.accessibility || formState.errors.access_table || formState.errors.info
  const componentsErrors = formState.errors.components;

  // if no course return loading
  if (!instance || formLoading) {
    return <BASELoading />;
  }

  return (
    <FormProvider {...methods}>
      <div className="flex flex-col flex-auto w-full mb-64 px-24 sm:px-24">
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="secondary"
          textColor="secondary"
          variant="scrollable"
          scrollButtons="auto"
          classes={{ root: 'w-full h-64 border-b-1' }}
        >
          <Tab 
            className="h-64" 
            label={
              <>
                {basicErrors ? (
                    <Badge color="error" variant="dot">
                      Basic Info
                    </Badge>
                  ) : (
                    "Basic Info"
                  )  
                }
              </>
            }
          />
          <Tab 
            className="h-64"
            label={
              <>
                {devErrors ? (
                    <Badge color="error" variant="dot">
                      Dev Info
                    </Badge>
                  ) : (
                    "Dev Info"
                  )  
                }
              </>
            }
          />
          <Tab 
            className="h-64"
            label={
              <>
                {accessibilityErrors ? (
                    <Badge color="error" variant="dot">
                      508 Compliance
                    </Badge>
                  ) : (
                    "508 Compliance"
                  )  
                }
              </>
            }
          />
          <Tab 
            className="h-64"
            label={
              <>
                {componentsErrors ? (
                    <Badge color="error" variant="dot">
                      Components
                    </Badge>
                  ) : (
                    "Components"
                  )  
                }
              </>
            }
          />
        </Tabs>
        <div className="p-16 sm:p-24 max-w-4xl">
          <div className={tabValue !== 0 ? 'hidden' : ''}>
            <BasicInfoForm />
          </div>
          <div className={tabValue !== 1 ? 'hidden' : ''}>
            <DevInfoForm />
          </div>
          <div className={tabValue !== 2 ? 'hidden' : ''}>
            <AccessibilityForm />
          </div>
          <div className={tabValue !== 3 ? 'hidden' : ''}>
            <ComponentsForm />
          </div>
        </div>
      </div>
    </FormProvider>
  );
}
export default TemplateForm;
